import { useEffect, useState } from 'react';
import { formatCents, type ShiftReportRow } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { reportsApi } from '../../api/resources.js';
import { ApiError } from '../../lib/apiClient.js';
import { Banner } from '../../components/Banner.js';
import { SortableColumnHeader } from '../../components/table/SortableColumnHeader.js';
import { FilterSummary, RangeFilterInput, SelectFilterInput, TextFilterInput } from '../../components/table/FilterInputs.js';
import { useTableSort } from '../../lib/dataTable/useTableSort.js';
import { useColumnFilters } from '../../lib/dataTable/useColumnFilters.js';
import { moneyRangeColumn, selectColumn, textContainsColumn } from '../../lib/dataTable/filters.js';
import { formatDateTime, daysAgoISO, todayISO } from '../../lib/format.js';

/** "Sobrante"/"Faltante"/"Sin diferencia" as a filterable category — never a raw signed amount, matching how `VarianceCell` already presents this column (see docs/decisions/2026-07-31-shift-variance-presentation.md). */
function varianceCategory(cents: number | null): string {
  if (cents == null || cents === 0) return 'none';
  return cents > 0 ? 'over' : 'short';
}

/**
 * Variance is highlighted, never editorialized: same visual treatment for
 * "over" and "short" (a neutral attention color, not green-for-surplus /
 * red-for-shortfall), and no wording that implies fault. See spec section 7
 * and docs/decisions/2026-07-31-shift-variance-presentation.md.
 */
function VarianceCell({
  cents,
  t,
}: {
  cents: number | null;
  t: ReturnType<typeof useI18n>['t'];
}) {
  if (cents == null) return <span>—</span>;
  if (cents === 0) {
    return <span className="badge badge--ok numeric">{t('shifts.varianceNone')}</span>;
  }
  const label = cents > 0 ? t('shifts.varianceOver', { amount: formatCents(cents) }) : t('shifts.varianceShort', { amount: formatCents(Math.abs(cents)) });
  return <span className="badge badge--warn numeric">{label}</span>;
}

export function ShiftsPage() {
  const { t } = useI18n();
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [rows, setRows] = useState<ShiftReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportsApi
      .shifts(from, to)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const columnFilters = useColumnFilters(rows, {
    staffName: textContainsColumn<ShiftReportRow>((r) => r.staffName),
    terminalName: textContainsColumn<ShiftReportRow>((r) => r.terminalName),
    openingBalanceCents: moneyRangeColumn<ShiftReportRow>((r) => r.openingBalanceCents),
    closingBalanceCents: moneyRangeColumn<ShiftReportRow>((r) => r.closingBalanceCents),
    expectedCashCents: moneyRangeColumn<ShiftReportRow>((r) => r.expectedCashCents),
    varianceCents: selectColumn<ShiftReportRow>((r) => varianceCategory(r.varianceCents)),
  });

  const sort = useTableSort(columnFilters.rows, {
    staffName: { type: 'string', value: (r: ShiftReportRow) => r.staffName },
    terminalName: { type: 'string', value: (r: ShiftReportRow) => r.terminalName },
    openedAt: { type: 'date', value: (r: ShiftReportRow) => r.openedAt },
    closedAt: { type: 'date', value: (r: ShiftReportRow) => r.closedAt },
    openingBalanceCents: { type: 'number', value: (r: ShiftReportRow) => r.openingBalanceCents },
    closingBalanceCents: { type: 'number', value: (r: ShiftReportRow) => r.closingBalanceCents },
    expectedCashCents: { type: 'number', value: (r: ShiftReportRow) => r.expectedCashCents },
    varianceCents: { type: 'number', value: (r: ShiftReportRow) => r.varianceCents },
  });
  const visibleRows = sort.rows;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="page-header">
        <div>
          <h1>{t('shifts.title')}</h1>
          <p>{t('shifts.subtitle')}</p>
        </div>
      </div>

      <Banner kind="warn">{t('shifts.confidential')}</Banner>

      <div className="card toolbar">
        <label htmlFor="shifts-from">
          {t('reports.from')}
          <input id="shifts-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label htmlFor="shifts-to">
          {t('reports.to')}
          <input id="shifts-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>

      {error && <Banner kind="danger">{error}</Banner>}

      <FilterSummary activeCount={columnFilters.activeCount} onClear={columnFilters.clear} />

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p>{t('shifts.noData')}</p>
      ) : visibleRows.length === 0 ? (
        <p>{t('table.noMatches')}</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <SortableColumnHeader label={t('shifts.staff')} ariaSort={sort.ariaSortFor('staffName')} onSort={() => sort.toggleSort('staffName')} />
                <SortableColumnHeader
                  label={t('shifts.terminal')}
                  ariaSort={sort.ariaSortFor('terminalName')}
                  onSort={() => sort.toggleSort('terminalName')}
                />
                <SortableColumnHeader label={t('shifts.opened')} ariaSort={sort.ariaSortFor('openedAt')} onSort={() => sort.toggleSort('openedAt')} />
                <SortableColumnHeader label={t('shifts.closed')} ariaSort={sort.ariaSortFor('closedAt')} onSort={() => sort.toggleSort('closedAt')} />
                <SortableColumnHeader
                  label={t('shifts.openingBalance')}
                  align="right"
                  ariaSort={sort.ariaSortFor('openingBalanceCents')}
                  onSort={() => sort.toggleSort('openingBalanceCents')}
                />
                <SortableColumnHeader
                  label={t('shifts.closingBalance')}
                  align="right"
                  ariaSort={sort.ariaSortFor('closingBalanceCents')}
                  onSort={() => sort.toggleSort('closingBalanceCents')}
                />
                <SortableColumnHeader
                  label={t('shifts.expectedCash')}
                  align="right"
                  ariaSort={sort.ariaSortFor('expectedCashCents')}
                  onSort={() => sort.toggleSort('expectedCashCents')}
                />
                <SortableColumnHeader
                  label={t('shifts.variance')}
                  ariaSort={sort.ariaSortFor('varianceCents')}
                  onSort={() => sort.toggleSort('varianceCents')}
                />
              </tr>
              <tr className="table-filter-row">
                <th>
                  <TextFilterInput
                    id="shifts-filter-staff"
                    column={t('shifts.staff')}
                    value={columnFilters.values.staffName as string}
                    onChange={(value) => columnFilters.setValue('staffName', value)}
                  />
                </th>
                <th>
                  <TextFilterInput
                    id="shifts-filter-terminal"
                    column={t('shifts.terminal')}
                    value={columnFilters.values.terminalName as string}
                    onChange={(value) => columnFilters.setValue('terminalName', value)}
                  />
                </th>
                <th />
                <th />
                <th className="numeric">
                  <RangeFilterInput
                    id="shifts-filter-opening"
                    column={t('shifts.openingBalance')}
                    value={columnFilters.values.openingBalanceCents as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('openingBalanceCents', value)}
                    affix={{ symbol: '$', position: 'prefix' }}
                    placeholder="0.00"
                  />
                </th>
                <th className="numeric">
                  <RangeFilterInput
                    id="shifts-filter-closing"
                    column={t('shifts.closingBalance')}
                    value={columnFilters.values.closingBalanceCents as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('closingBalanceCents', value)}
                    affix={{ symbol: '$', position: 'prefix' }}
                    placeholder="0.00"
                  />
                </th>
                <th className="numeric">
                  <RangeFilterInput
                    id="shifts-filter-expected"
                    column={t('shifts.expectedCash')}
                    value={columnFilters.values.expectedCashCents as { min: string; max: string }}
                    onChange={(value) => columnFilters.setValue('expectedCashCents', value)}
                    affix={{ symbol: '$', position: 'prefix' }}
                    placeholder="0.00"
                  />
                </th>
                <th>
                  <SelectFilterInput
                    id="shifts-filter-variance"
                    column={t('shifts.variance')}
                    value={columnFilters.values.varianceCents as string}
                    onChange={(value) => columnFilters.setValue('varianceCents', value)}
                    options={[
                      { value: 'over', label: t('shifts.varianceCategoryOver') },
                      { value: 'short', label: t('shifts.varianceCategoryShort') },
                      { value: 'none', label: t('shifts.varianceNone') },
                    ]}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.shiftId}>
                  <td>{row.staffName}</td>
                  <td>{row.terminalName}</td>
                  <td className="numeric">{formatDateTime(row.openedAt)}</td>
                  <td className="numeric">{row.closedAt ? formatDateTime(row.closedAt) : t('shifts.statusOpen')}</td>
                  <td className="numeric">{formatCents(row.openingBalanceCents)}</td>
                  <td className="numeric">{row.closingBalanceCents != null ? formatCents(row.closingBalanceCents) : '—'}</td>
                  <td className="numeric">{row.expectedCashCents != null ? formatCents(row.expectedCashCents) : '—'}</td>
                  <td>
                    <VarianceCell cents={row.varianceCents} t={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
